<template>
  <div class="layout-root">
    <div class="site-pusher" :class="{ 'is-pushed': isFeedOpen }">
      <div class="layout">
        <Header />
        <Drops />

        <!-- Loading Overlay (positioned after drops) -->
        <div v-if="globalLoading" class="content-loading-screen"></div>

        <div v-else class="main-body">
          <div :class="contentWrapperClass">
            <div v-if="shouldShowUpgradePromoBanner" class="homepage-primary-promotion">
              <UpgradePromoBanner compact-on-mobile />
            </div>
            <div v-if="shouldShowEventBanner" class="homepage-event-promotion">
              <EventBanner />
            </div>

            <!-- Promo Banners for Cases Page -->
            <PromoBanners v-if="route.name === 'cases'" class="homepage-secondary-promotions" />

            <CaseFilter v-if="showFilters" />
            <main class="content">
              <RouterView />
            </main>
          </div>
        </div>

        <XBanner />
        <DepositStreakWidget />

        <Stats />
        <Footer />
        
        <!-- Modals placed here so they move with the site or stay? Usually modals are fixed to viewport. 
             If we want them to push, they go here. If we want them to overlay regardless, they can stay or go outside.
             Let's keep them here for simplicity unless they have z-index issues. -->
        <LoginModal v-if="showLoginModal" />
        <DepositModal v-if="showDepositModal || depositCompletion" />
        <DepositStreakModal v-if="showDepositStreakModal" />
        <WithdrawExchangeModal v-if="showExchangeModal" />
        <CasePrizeModal v-if="showCasePrizeModal" />
        <PromoAnnouncementModal v-if="isPromoAnnouncementEnabled && showPromoModal" />
        <EmailVerifiedModal v-if="showEmailVerifiedModal" />
        <OddsHistoryModal v-if="showOddsHistoryModal" />
        <EmailVerificationWidget 
          v-if="profile && route.name !== 'login'" 
          :userProfile="profile"
          @save-email="handleSaveEmail"
        />
      </div>
    </div>

    <!-- Feed is outside the pusher -->
    <ActivityFeed
      v-if="isFeedOpen"
      class="activity-feed-global"
      :class="{ 'is-open': isFeedOpen }"
    />
  </div>
</template>

<script setup lang="ts">
// Components
import Header from './components/Header/Header.vue'
import Stats from './components/Stats/Stats.vue'
import Footer from './components/Footer/Footer.vue'
import Drops from './components/Drops/Drops.vue'
import EventBanner from '@/components/PromotionalEvent/EventBanner.vue'
import UpgradePromoBanner from '@/components/UpgradePromoBanner.vue'
import CaseFilter from './components/CaseFilter/CaseFilter.vue'
import PromoBanners from '@/components/PromoBanners.vue'
import XBanner from '@/components/XBanner.vue'
import DepositStreakWidget from '@/components/DepositStreakWidget.vue'
// Modals
const LoginModal = defineAsyncComponent(() => import('@/components/Modals/LoginModal.vue'))
const DepositModal = defineAsyncComponent(
  () => import('@/components/Modals/DepositModal/DepositModal.vue'),
)
const DepositStreakModal = defineAsyncComponent(
  () => import('@/components/Modals/DepositStreakModal.vue'),
)
const WithdrawExchangeModal = defineAsyncComponent(
  () => import('@/components/Modals/WithdrawExchangeModal.vue'),
)
const CasePrizeModal = defineAsyncComponent(
  () => import('@/components/Modals/CasePrizeModal/CasePrizeModal.vue'),
)
const PromoAnnouncementModal = defineAsyncComponent(
  () => import('@/components/Modals/PromoAnnouncementModal.vue'),
)
const EmailVerifiedModal = defineAsyncComponent(
  () => import('@/components/Modals/EmailVerifiedModal.vue'),
)
const OddsHistoryModal = defineAsyncComponent(
  () => import('@/components/Modals/OddsHistoryModal/OddsHistoryModal.vue'),
)
const EmailVerificationWidget = defineAsyncComponent(
  () => import('@/components/Modals/EmailVerificationWidget.vue'),
)
const ActivityFeed = defineAsyncComponent(
  () => import('@/components/ActivityFeed/ActivityFeed.vue'),
)

// Store
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/store/app'
import { useProfileStore } from '@/store/profile'
import { useDepositStore } from '@/store/deposit'
import { useFeatureFlagsStore } from '@/store/featureFlags'
import { useActivityFeedStore } from '@/store/activityFeed'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { onMounted, watch, computed, ref, defineAsyncComponent } from 'vue'
import { PROMO_MODAL_DISMISS_KEY, PROMO_MODAL_DISMISS_DURATION_MS } from '@/constants/storageKeys'
import { useReferralCode as useReferralCodeApi, ApiError } from '@/services/api'
import { RouteName } from '@/router/types'
import { FeatureFlag } from '@/types/featureFlags'
import { SHOW_ZERO_EDGE_MARKETING } from '@/utils/upgrade'

const route = useRoute()
const router = useRouter()
const siteName = 'Skinwhales'
const baseUrl = (import.meta.env.VITE_SITE_URL ?? 'https://skinwhales.com').replace(/\/$/, '')
const defaultDescription =
  'Skinwhales delivers a CS2-inspired case opening experience with live drops, provably fair systems, and community events.'
const defaultOgImage = new URL('/favicon-96x96.png', `${baseUrl}/`).toString()

useHead(() => {
  const title = route.meta.title ?? siteName
  const description = route.meta.description ?? defaultDescription
  const keywordsArray = Array.isArray(route.meta.keywords) ? route.meta.keywords : undefined
  const keywords = keywordsArray?.join(', ')
  const ogImage = route.meta.ogImage ?? defaultOgImage
  const ogImageAlt = route.meta.ogImageAlt
  const twitterImageAlt = route.meta.twitterImageAlt ?? ogImageAlt
  const ogType = route.meta.ogType ?? 'website'
  const canonicalPath = route.meta.canonicalPath ?? route.fullPath

  let canonical = baseUrl
  try {
    canonical = new URL(canonicalPath, `${baseUrl}/`).toString()
  } catch (error) {
    console.warn('Unable to construct canonical URL', error)
  }

  const robots = route.meta.robots ?? 'index,follow'

  const meta: Array<Record<string, string>> = [
    { name: 'description', content: description },
    { name: 'robots', content: robots },
    { property: 'og:site_name', content: siteName },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: ogType },
    { property: 'og:url', content: canonical },
    { property: 'og:image', content: ogImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: ogImage },
  ]

  if (keywords) meta.push({ name: 'keywords', content: keywords })
  if (ogImageAlt) meta.push({ property: 'og:image:alt', content: ogImageAlt })
  if (twitterImageAlt) meta.push({ name: 'twitter:image:alt', content: twitterImageAlt })

  return {
    title,
    meta,
    link: [{ rel: 'canonical', href: canonical }],
  }
})

const app = useAppStore()
const {
  showFilters,
  globalLoading,
  showLoginModal,
  showDepositModal,
  showExchangeModal,
  showCasePrizeModal,
  showPromoModal,
  showDepositStreakModal,
  showEmailVerifiedModal,
  showOddsHistoryModal,
} = storeToRefs(app)
const profileStore = useProfileStore()
const { profile } = storeToRefs(profileStore)
const depositStore = useDepositStore()
const { completion: depositCompletion } = storeToRefs(depositStore)
const featureFlagsStore = useFeatureFlagsStore()
const isPromoAnnouncementEnabled = computed(() =>
  featureFlagsStore.isEnabled(FeatureFlag.MODAL_PROMO_ANNOUNCEMENT),
)

const activityFeedStore = useActivityFeedStore()
const { isOpen: isFeedOpen } = storeToRefs(activityFeedStore)

const pendingReferralCode = ref<string | null>(null)
const isApplyingReferralCode = ref(false)
const REFERRAL_STORAGE_KEY = 'pendingReferralCode'

const shouldShowEventBanner = computed(() => {
  return route.name === 'cases' || route.name === 'events'
})

const shouldShowUpgradePromoBanner = computed(() => {
  return (
    SHOW_ZERO_EDGE_MARKETING &&
    route.name === RouteName.CASES &&
    featureFlagsStore.isEnabled(FeatureFlag.GAME_UPGRADE)
  )
})

const contentWrapperClass = computed(() => {
  if (route.name === 'case-detail' || route.path.startsWith('/daily-free/')) {
    return 'content-wrapper content-wrapper--full-width'
  }
  return 'content-wrapper'
})

const eligibleRouteNames = new Set<string>([RouteName.CASES])

function isPromoEligibleForRoute(routeName: unknown) {
  return typeof routeName === 'string' && eligibleRouteNames.has(routeName)
}

function hasPromoCooldownElapsed() {
  if (typeof window === 'undefined') return false

  const stored = window.localStorage.getItem(PROMO_MODAL_DISMISS_KEY)
  if (!stored) return true

  const dismissedAt = Number.parseInt(stored, 10)
  if (Number.isNaN(dismissedAt)) return true

  return Date.now() - dismissedAt >= PROMO_MODAL_DISMISS_DURATION_MS
}

function evaluatePromoModalVisibility() {
  if (!isPromoAnnouncementEnabled.value) {
    app.setShowPromoModal(false)
    return
  }

  if (!isPromoEligibleForRoute(route.name)) {
    app.setShowPromoModal(false)
    return
  }

  if (hasPromoCooldownElapsed()) {
    app.setShowPromoModal(true)
    return
  }

  app.setShowPromoModal(false)
}

async function handleSaveEmail(newEmail: string) {
  try {
    await profileStore.saveEmail(newEmail)
  } catch (error) {
    console.error("Failed to save email from main layout:", error)
    throw error
  }
}

onMounted(() => {
  hydrateReferralFromStorage()
  if (isPromoAnnouncementEnabled.value) {
    evaluatePromoModalVisibility()
  } else {
    app.setShowPromoModal(false)
  }
  maybeShowEmailVerifiedModal(route.query.emailVerified)
  handleReferralFromQuery(route.query.ref)
})

watch(
  () => route.path,
  () => {
    evaluatePromoModalVisibility()
  },
)

watch(isPromoAnnouncementEnabled, (enabled) => {
  if (enabled) {
    evaluatePromoModalVisibility()
  } else {
    app.setShowPromoModal(false)
  }
})

watch(
  () => route.query.emailVerified,
  (value) => {
    maybeShowEmailVerifiedModal(value)
  },
)

watch(
  () => route.query.ref,
  (value) => {
    handleReferralFromQuery(value)
  },
)

watch(profile, () => {
  applyReferralCodeIfPossible()
})

function maybeShowEmailVerifiedModal(value: unknown) {
  const normalized = Array.isArray(value) ? value.find((item) => item != null) : value
  if (typeof normalized !== 'string') return

  if (normalized.toLowerCase() !== 'true') return

  app.setShowEmailVerifiedModal(true)

  const nextQuery = { ...route.query }
  delete nextQuery.emailVerified
  router.replace({ query: nextQuery }).catch(() => {})
}

function normalizeReferralQueryValue(value: unknown): string | null {
  if (Array.isArray(value)) {
    const firstValid = value.find((item) => typeof item === 'string' && item.trim().length > 0)
    return typeof firstValid === 'string' ? firstValid.trim() : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }

  return null
}

function handleReferralFromQuery(value: unknown) {
  const normalized = normalizeReferralQueryValue(value)
  if (!normalized) return

  pendingReferralCode.value = normalized
  persistReferralCode(normalized)
  applyReferralCodeIfPossible()
}

function hydrateReferralFromStorage() {
  if (typeof window === 'undefined') return
  if (pendingReferralCode.value) return

  const stored = window.localStorage.getItem(REFERRAL_STORAGE_KEY)
  if (stored && stored.trim().length > 0) {
    pendingReferralCode.value = stored.trim()
  }
}

function persistReferralCode(code: string) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(REFERRAL_STORAGE_KEY, code)
}

function clearPersistedReferralCode() {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(REFERRAL_STORAGE_KEY)
}

async function applyReferralCodeIfPossible() {
  const code = pendingReferralCode.value
  const currentProfile = profile.value

  if (!code) return
  if (!currentProfile) return
  if (currentProfile.usedReferralCode) {
    pendingReferralCode.value = null
    clearPersistedReferralCode()
    removeReferralQueryParam()
    return
  }
  if (isApplyingReferralCode.value) return

  isApplyingReferralCode.value = true
  try {
    await useReferralCodeApi(code)
    pendingReferralCode.value = null
    clearPersistedReferralCode()
    await profileStore.fetchProfile()
    removeReferralQueryParam()
  } catch (error) {
    console.error('Failed to apply referral code from query parameter', error)

    if (error instanceof ApiError) {
      if (error.status === 401) {
        // Keep the pending code so we can retry once the user authenticates again
        return
      }

      if (error.status === 400 || error.status === 404 || error.status === 409) {
        pendingReferralCode.value = null
        clearPersistedReferralCode()
        removeReferralQueryParam()
      }
    }
  } finally {
    isApplyingReferralCode.value = false
  }
}

function removeReferralQueryParam() {
  if (!('ref' in route.query)) return

  const nextQuery = { ...route.query }
  delete nextQuery.ref
  router.replace({ query: nextQuery }).catch(() => {})
}
</script>

<style scoped lang="scss">
.content-loading-screen {
  @include page-shell();
  min-height: 100vh;
  padding-top: $space-xl;
  animation: fadeOut 0.3s ease-out forwards;

  @include mobile {
    padding: 0;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

.layout {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;

  @include mobile {
    padding-bottom: calc(60px + env(safe-area-inset-bottom));
  }

  .content {
    min-height: 60vh;
  }

  .content-wrapper {
    @include page-frame();
    transition: margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &--full-width {
      max-width: none;
      padding: 0;

      @media (max-width: 768px) {
        padding: 0;
      }
    }
  }
}

.main-body {
  display: flex;
  position: relative;
  flex: 1;
}

.layout-root {
  position: relative;
  overflow-x: hidden;
  width: 100%;
  min-height: 100vh;
}

.homepage-event-promotion,
.homepage-secondary-promotions {
  @include mobile {
    display: none;
  }
}

.site-pusher {
  transition: margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: margin-right;
  width: auto;
  min-height: 100vh;
  position: relative;
  
  &.is-pushed {
    margin-right: 320px;
    
    @include mobile {
      margin-right: 0;
    }
  }
}

.activity-feed-global {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 320px;
  background: #0f111a;
  z-index: 2100; // Above everything
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: -5px 0 20px rgba(0,0,0,0.5);

  &.is-open {
    transform: translateX(0);
  }

  @include mobile {
    display: none !important;
  }
}
</style>
